import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';
import { ViewKey } from './GetRenderView';
import {
  Detail1ResponseType,
  Detail2ResponseType,
  MedicalHealthInformationData,
  QuestionAnswer,
  QuestionnaireResponse
} from './types/MedicalHealthInformation';
import { fetchAuthSession } from 'aws-amplify/auth';
import { NoListError } from './errors/errors';
import { web3APIClient } from '@/client/axios';
import { MEDICAL_HEALTH_INFORMATION_NAME } from './constants/constants';

import { executeGet } from '@/client/rest';

const defaultActiveMedicalHealthInformationData: MedicalHealthInformationData = {
  facility_id: 0,
  facility_name: '',
  reserve_id: 0,
  outpatient_id: '',
  outpatient_name: '',
  reserve_date: '',
  reserve_time: '',
  questionnaire_response_list: []
};

const defaultActiveQuestionnaireResponse: QuestionnaireResponse = {
  information_kbn: '',
  complete_flag: '',
  questionnaire_response_id: ''
};

type ContextType = {
  // userId
  cognitoId: string;

  // ページ管理
  activeStep: ViewKey;
  setActiveStep: Dispatch<SetStateAction<ViewKey>>;

  // fetcher
  fetchMedicalHealthInformationList: () => Promise<void>;
  fetchMedicalHealthInformationDetail1: (questionnaireResponseId: string) => Promise<void>;
  fetchMedicalHealthInformationDetail2: (questionnaireResponseId: string, facilityId: string) => Promise<void>;

  // 一覧
  medicalHealthInformationList: MedicalHealthInformationData[];
  // アクティブな詳細
  activeMedicalHealthInformation: MedicalHealthInformationData;
  setActiveMedicalHealthInformation: Dispatch<SetStateAction<MedicalHealthInformationData>>;
  // アクティブなQuestionnaireResponse
  activeQuestionnaireResponse: QuestionnaireResponse;
  setActiveQuestionnaireResponse: Dispatch<SetStateAction<QuestionnaireResponse>>;
  // 詳細の回答一覧
  questionAnswerList: QuestionAnswer[];
};

const Context = createContext<ContextType | null>(null);

export const MedicalHealthInformationContextProvider = ({ children }: { children: ReactNode }) => {
  const [cognitoId, setCognitoId] = useState('');
  const [activeStep, setActiveStep] = useState<ViewKey>(MEDICAL_HEALTH_INFORMATION_NAME);
  const [medicalHealthInformationList, setMedicalHealthInformationList] = useState<MedicalHealthInformationData[]>([]);
  const [activeMedicalHealthInformation, setActiveMedicalHealthInformation] = useState<MedicalHealthInformationData>(
    defaultActiveMedicalHealthInformationData
  );
  const [activeQuestionnaireResponse, setActiveQuestionnaireResponse] = useState<QuestionnaireResponse>(
    defaultActiveQuestionnaireResponse
  );
  const [questionAnswerList, setQuestionAnswerList] = useState<QuestionAnswer[]>([]);

  /**
   * 一覧取得
   */
  const fetchMedicalHealthInformationList = useCallback(async () => {
    const session = await fetchAuthSession();
    const idtoken = session.tokens?.idToken?.toString();
    const userSub = session.userSub;
    if (!idtoken || !userSub) return;
    const result = await executeGet<MedicalHealthInformationData[]>({
      apiName: 'medirecoWebApi',
      path: '/medical/health-info/list',
      options: {
        headers: {
          authorization: idtoken
        }
      }
    });

    if (result.length === 0) {
      throw new NoListError();
    }
    setMedicalHealthInformationList(result);
    setCognitoId(userSub);
  }, []);

  /**
   * 詳細1取得
   */
  const fetchMedicalHealthInformationDetail1 = useCallback(async (questionnaireResponseId: string) => {
    const session = await fetchAuthSession();
    const idtoken = session.tokens?.idToken?.toString();
    if (!idtoken) return;
    const data = await executeGet<Detail1ResponseType>({
      apiName: 'medirecoWebApi',
      path: '/medical/health-info/detail1',
      options: {
        queryParams: { questionnaireResponseId },
        headers: {
          authorization: idtoken
        }
      }
    });
    setQuestionAnswerList(data?.information_list ?? []);
  }, []);

  /**
   * 詳細2取得
   */
  const fetchMedicalHealthInformationDetail2 = useCallback(
    async (questionnaireResponseId: string, facilityId: string) => {
      const session = await fetchAuthSession({ forceRefresh: true }); // 毎回refreshが必要
      const idToken = session.tokens?.idToken ?? '';
      const config = {
        headers: { Authorization: idToken.toString() },
        params: {
          questionnaireResponseId,
          facilityId
        }
      };

      const response = (await web3APIClient.get('medical/health-info/detail2', config)) as unknown as {
        data: Detail2ResponseType;
      };

      setQuestionAnswerList(response?.data?.informationList ?? []);
    },
    []
  );

  const value = {
    cognitoId,
    activeStep,
    setActiveStep,
    fetchMedicalHealthInformationList,
    fetchMedicalHealthInformationDetail1,
    fetchMedicalHealthInformationDetail2,
    medicalHealthInformationList,
    activeMedicalHealthInformation,
    setActiveMedicalHealthInformation,
    activeQuestionnaireResponse,
    setActiveQuestionnaireResponse,
    questionAnswerList
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useMedicalHealthInformationContext = () => {
  const context = useContext(Context);
  if (!context) throw new Error('no context');

  return context;
};
